import Button from '../ui/Button'
import Input from '../ui/Input'
import { useLang } from '../../contexts/LangContext'
import { ATHENS_POSTAL_CODES, type OnboardingAreaDraft } from '../../lib/providerOnboarding'

interface ServiceAreasStepProps {
  areas: OnboardingAreaDraft[]
  pendingPostalCode: string
  saving: boolean
  onPendingPostalCodeChange: (value: string) => void
  onAddArea: () => void
  onQuickAdd: (postalCode: string) => void
  onUpdateArea: (index: number, field: keyof OnboardingAreaDraft, value: string) => void
  onRemoveArea: (index: number) => void
  onBack: () => void
  onNext: () => void
}

const POSTAL_CODE_DATALIST_ID = 'athens-postal-codes'

export default function ServiceAreasStep({
  areas,
  pendingPostalCode,
  saving,
  onPendingPostalCodeChange,
  onAddArea,
  onQuickAdd,
  onUpdateArea,
  onRemoveArea,
  onBack,
  onNext,
}: ServiceAreasStepProps) {
  const { t } = useLang()

  return (
    <div className="space-y-8">
      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <Input
              type="text"
              inputMode="numeric"
              list={POSTAL_CODE_DATALIST_ID}
              label={t('Ταχυδρομικός κώδικας', 'Postal code')}
              placeholder="10431"
              value={pendingPostalCode}
              onChange={(event) => onPendingPostalCodeChange(event.target.value)}
              hint={t(
                'Πρόσθεσε περιοχές που μπορείς να εξυπηρετείς. Μπορείς να ξεκινήσεις με Αθήνα και γύρω περιοχές.',
                'Add the areas you can serve. You can start with Athens and nearby postcodes.',
              )}
            />
            <datalist id={POSTAL_CODE_DATALIST_ID}>
              {ATHENS_POSTAL_CODES.map((postalCode) => (
                <option key={postalCode} value={postalCode} />
              ))}
            </datalist>
          </div>

          <Button type="button" size="lg" onClick={onAddArea}>
            {t('Προσθήκη περιοχής', 'Add area')}
          </Button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {ATHENS_POSTAL_CODES.map((postalCode) => (
            <button
              key={postalCode}
              type="button"
              onClick={() => onQuickAdd(postalCode)}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              {postalCode}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {areas.length === 0 ? (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            {t('Δεν έχεις προσθέσει ακόμη service areas.', 'You have not added any service areas yet.')}
          </div>
        ) : (
          areas.map((area, index) => (
            <article key={`${area.postalCode}-${index}`} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-700">
                      {area.postalCode}
                    </span>
                    <p className="text-sm text-slate-500">
                      {t('Επεξεργάσου το όνομα περιοχής ή την πόλη αν χρειάζεται.', 'Edit the area name or city if needed.')}
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      type="text"
                      label={t('Όνομα περιοχής', 'Area name')}
                      placeholder={t('Π.χ. Κέντρο Αθήνας', 'E.g. Athens Centre')}
                      value={area.areaName}
                      onChange={(event) => onUpdateArea(index, 'areaName', event.target.value)}
                    />
                    <Input
                      type="text"
                      label={t('Πόλη', 'City')}
                      placeholder={t('Αθήνα', 'Athens')}
                      value={area.city}
                      onChange={(event) => onUpdateArea(index, 'city', event.target.value)}
                    />
                  </div>
                </div>

                <Button type="button" variant="ghost" onClick={() => onRemoveArea(index)}>
                  {t('Αφαίρεση', 'Remove')}
                </Button>
              </div>
            </article>
          ))
        )}
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          {t('Πίσω', 'Back')}
        </Button>
        <Button type="button" size="lg" loading={saving} onClick={onNext}>
          {t('Ολοκλήρωση onboarding', 'Complete onboarding')}
        </Button>
      </div>
    </div>
  )
}
